import { useMutation, useQuery } from "@tanstack/react-query";
import { useAlert } from "context/alert";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createHeaderFooterImage,
  deleteHeaderFooterImage,
  fetchHeaderFooterImageList,
} from "services/headerFooterDetail";
import { getZoneList } from "../../redux/action/drop_list_action";
import Select from "react-select";
import zoneLocalStorageId from "utils/zone";
import { STALE_TIME } from "utils/constants";
import companyLocalStorageId from "utils/companyID";
import "./UploadImages.css";
const MAX_HEIGHT = 177;
const MAX_IMAGE_SIZE = 1024 * 1024;

const UploadImages = ({ onClose }) => {
  const { showAlert } = useAlert();
  const dispatch = useDispatch();

  const [newFilePreview, setNewFilePreview] = useState(null);
  const [newFileType, setNewFileType] = useState(null);

  const [placement, setPlacement] = useState("");
  const headerChecked = placement === "header";
  const footerChecked = placement === "footer";
  const watermarkChecked = placement === "watermark";
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState(false);
  const [fileErrorMessage, setFileErrorMessage] = useState("");
  const [fileData, setFileData] = useState("");
  const [selectedZoneId, setSelectedZoneId] = useState(null);
  const [zoneError, setZoneError] = useState(false);
  const [checkboxError, setCheckboxError] = useState(false);

  const [showAdjust, setShowAdjust] = useState(false);
  const [originalImage, setOriginalImage] = useState(null);
  const [imgNaturalWidth, setImgNaturalWidth] = useState(0);
  const [imgNaturalHeight, setImgNaturalHeight] = useState(0);
  const [cropY, setCropY] = useState(0);
  const [cropHeight, setCropHeight] = useState(MAX_HEIGHT);
  const [scale, setScale] = useState(100);

  const canvasRef = useRef(null);
  const previewCanvasRef = useRef(null);

  const jwtAuthreducer = useSelector((state) => state.jwtAuthreducer);
  const { zone_list } = useSelector((state) => state.dropListReducer);

  const _token =
    jwtAuthreducer?.token_values?.sub &&
    JSON.parse(jwtAuthreducer?.token_values?.sub?.replace(/'/g, '"'));

  const { mutate: addHeaderFooterImageMutate } = useMutation(
    createHeaderFooterImage,
    {
      onSuccess: () => {
        showAlert("Image uploaded successfully!", "success");
        onClose(true);
      },
      onError: () => {
        showAlert("Something went wrong!", "danger");
      },
    },
  );

  const { mutate: deleteHeaderFooterImageMutate } = useMutation(
    deleteHeaderFooterImage,
    {
      onSuccess: () => {
        showAlert("Image deleted successfully!", "success");
        onClose(true);
      },
      onError: () => {
        showAlert("Something went wrong!", "danger");
      },
    },
  );

  const { data } = useQuery(
    [
      "fetch-report-header-footer-list",
      headerChecked,
      footerChecked,
      watermarkChecked,
      selectedZoneId,
    ],
    () =>
      fetchHeaderFooterImageList({
        zone_id: selectedZoneId || zoneLocalStorageId(),
        is_active: true,
        created_by: jwtAuthreducer.user.employee_code_id,
        header: headerChecked,
        footer: footerChecked,
        watermark_image: watermarkChecked,
      }),
    {
      enabled: !!((headerChecked || footerChecked || watermarkChecked) && selectedZoneId),
      staleTime: STALE_TIME,
      onError: (err) => {
        console.error("Error fetching Header Footer:", err);
      },
    },
  );

  const imageData = data?.data;

  const applyAdjustment = () => {
    const canvas = canvasRef.current;
    if (!canvas || !originalImage) return;

    const scaledWidth = (imgNaturalWidth * scale) / 100;
    const scaledHeight = (imgNaturalHeight * scale) / 100;

    canvas.width = scaledWidth;
    canvas.height = MAX_HEIGHT;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.drawImage(
      originalImage,
      0,
      (cropY / 100) * scaledHeight,
      scaledWidth,
      MAX_HEIGHT,
      0,
      0,
      scaledWidth,
      MAX_HEIGHT,
    );

    const base64 = canvas.toDataURL("image/jpeg", 0.92);
    setNewFilePreview(base64);
    setFileData(base64.split(",")[1]);
    setShowAdjust(false);
    showAlert("Image adjusted successfully!", "success");
  };

  useEffect(() => {
    if (!showAdjust || !originalImage || !previewCanvasRef.current) return;

    const canvas = previewCanvasRef.current;
    const displayWidth = 500;
    const scaledWidth = (imgNaturalWidth * scale) / 100;
    const scaledHeight = (imgNaturalHeight * scale) / 100;
    const ratio = displayWidth / scaledWidth;

    canvas.width = displayWidth;
    canvas.height = MAX_HEIGHT * ratio;

    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.drawImage(
      originalImage,
      0,
      (cropY / 100) * scaledHeight,
      scaledWidth,
      MAX_HEIGHT,
      0,
      0,
      displayWidth,
      MAX_HEIGHT * ratio,
    );
  }, [
    showAdjust,
    cropY,
    scale,
    originalImage,
    imgNaturalWidth,
    imgNaturalHeight,
  ]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];

    if (!selectedFile) {
      setFile(null);
      setNewFilePreview(null);
      setNewFileType(null);
      setFileError(true);
      setFileErrorMessage("Please select a file.");
      return;
    }

    if (selectedFile.type.startsWith("image/") && selectedFile.size >= MAX_IMAGE_SIZE) {
      setFile(null);
      setNewFilePreview(null);
      setNewFileType(null);
      setFileData("");
      setFileError(true);
      setFileErrorMessage("Image must be under 1 MB.");
      e.target.value = "";
      return;
    }

    setNewFileType(selectedFile.type);
    setFileErrorMessage("");

    if (selectedFile.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result;
        const img = new Image();
        img.onload = () => {
          setImgNaturalWidth(img.naturalWidth);
          setImgNaturalHeight(img.naturalHeight);
          setOriginalImage(img);

          console.log("img.naturalHeight", img.naturalHeight);

          if (img.naturalHeight > MAX_HEIGHT && !watermarkChecked) {
            setCropY(0);
            setScale(100);
            setShowAdjust(true);
            setNewFilePreview(base64String);
            setFileData(base64String.split(",")[1]);
            setFile(selectedFile);
            setFileError(false);
          } else {
            setNewFilePreview(base64String);
            setFileData(base64String.split(",")[1]);
            setFile(selectedFile);
            setFileError(false);
          }
        };
        img.src = base64String;
      };
      reader.readAsDataURL(selectedFile);
    } else {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result;
        setFileData(base64String.split(",")[1]);
        setNewFilePreview(base64String);
      };
      reader.readAsDataURL(selectedFile);
      setFile(selectedFile);
      setFileError(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const hasCheckboxError = !headerChecked && !footerChecked && !watermarkChecked;
    const hasFileError =
      !file || (watermarkChecked && !file.type.startsWith("image/"));
    const hasZoneError = !selectedZoneId;

    setCheckboxError(hasCheckboxError);
    setFileError(hasFileError);
    setZoneError(hasZoneError);

    if (hasCheckboxError || hasFileError || hasZoneError) return;

    const payload = {
      header: headerChecked,
      footer: footerChecked,
      watermark_image: watermarkChecked,
      file_name: file.name,
      file_path: file.name,
      file_data: fileData,
      zone_id: selectedZoneId || zoneLocalStorageId() || Number(_token?.zone_id),
      is_active: true,
      created_by: jwtAuthreducer.user.employee_code_id,
      company_id: companyLocalStorageId(),
    };

    if (imageData && imageData.length > 0) {
      const firstImageId = imageData[0]?.id;
      if (firstImageId) {
        deleteHeaderFooterImageMutate(firstImageId);
      }
    }
    addHeaderFooterImageMutate(payload);
  };

  useEffect(() => {
    dispatch(getZoneList());
  }, [dispatch]);

  useEffect(() => {
    const localZoneId = zoneLocalStorageId() || Number(_token?.zone_id);
    if (!selectedZoneId && localZoneId) setSelectedZoneId(localZoneId);
  }, [selectedZoneId, _token?.zone_id]);

  const zoneOptions = zone_list?.zone_data?.map((item) => ({
    value: item.zone_id,
    label: item.zone_name,
    zone_id: item.zone_id,
  })) || [];

  return (
    <>
      <form onSubmit={handleSubmit}>
        <canvas ref={canvasRef} style={{ display: "none" }} />

        <div className="upload-images-form">
          <div className="flex-fill">
            <label className="form-label mb-2">
              Show in PDF <span className="text-danger">*</span>
            </label>
            <div className="upload-images-placements">
              <label className={`upload-images-placement ${headerChecked ? "active" : ""}`}>
                <input
                  type="radio"
                  id="headerCheckbox"
                  name="imagePlacement"
                  checked={headerChecked}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setPlacement("header");
                      setCheckboxError(false);
                    }
                  }}
                />
                <span className="upload-images-placement-dot" />
                <span><strong>Header</strong><small>Top of each page</small></span>
              </label>
              <label className={`upload-images-placement ${footerChecked ? "active" : ""}`}>
                <input
                  type="radio"
                  id="footerCheckbox"
                  name="imagePlacement"
                  checked={footerChecked}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setPlacement("footer");
                      setCheckboxError(false);
                    }
                  }}
                />
                <span className="upload-images-placement-dot" />
                <span><strong>Footer</strong><small>Bottom of each page</small></span>
              </label>
              <label className={`upload-images-placement ${watermarkChecked ? "active" : ""}`}>
                <input
                  type="radio"
                  id="watermarkCheckbox"
                  name="imagePlacement"
                  checked={watermarkChecked}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setPlacement("watermark");
                      setCheckboxError(false);
                    }
                  }}
                />
                <span className="upload-images-placement-dot" />
                <span><strong>Watermark</strong><small>Behind page content</small></span>
              </label>
            </div>
            {checkboxError && (
              <div className="text-danger mt-2">Select at least one PDF placement.</div>
            )}
          </div>
          <div className="flex-fill">
            <label className="form-label">
              Zone <span className="text-danger">*</span>
            </label>
            <Select
              options={zoneOptions}
              value={zoneOptions.find((option) => Number(option.zone_id) === Number(selectedZoneId)) || null}
              isClearable
              placeholder="Select Zone"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              onChange={(val) => {
                setSelectedZoneId(val?.zone_id || null);
                setZoneError(!val);
              }}
            />
            {zoneError && (
              <div className="text-danger mt-1">
                Zone selection is required.
              </div>
            )}
          </div>
        </div>

        <div className="upload-images-file mt-3">
          <div className="col-12">
            <label className="form-label">
              Upload Document <span className="text-danger">*</span>
              {!watermarkChecked && (
                <span className="text-muted ms-2" style={{ fontSize: "12px" }}>
                  (Image height must be ≤ {MAX_HEIGHT}px)
                </span>
              )}
            </label>
            <input
              type="file"
              className={`form-control ${fileError ? "is-invalid" : ""}`}
              onChange={handleFileChange}
              accept={watermarkChecked ? "image/*" : ".pdf,image/*"}
            />
            {fileError && (
              <div className="text-danger mt-1">
                {fileErrorMessage ||
                  (watermarkChecked
                    ? "Choose an image file for the watermark."
                    : "File is required.")}
              </div>
            )}
          </div>
        </div>

        <div className="mt-4">
          <button type="submit" className="btn btn-primary px-4">
            Save image settings
          </button>
        </div>

        {newFilePreview && !showAdjust && (
          <div className="mt-3" style={{ textAlign: "center" }}>
            <div
              style={{ marginBottom: "8px", fontWeight: "bold", color: "#555" }}
            >
              Selected File Preview:
            </div>
            {newFileType?.startsWith("image/") ? (
              <>
                <img
                  src={newFilePreview}
                  alt="Preview"
                  style={{
                    width: "500px",
                    objectFit: "contain",
                    border: "2px dashed #007bff",
                    borderRadius: "8px",
                    padding: "4px",
                  }}
                />
                {originalImage && originalImage.naturalHeight > MAX_HEIGHT && (
                  <div className="mt-2">
                    <button
                      type="button"
                      className="btn btn-warning btn-sm"
                      onClick={() => setShowAdjust(true)}
                    >
                      ✂️ Re-Adjust Image
                    </button>
                  </div>
                )}
              </>
            ) : newFileType === "application/pdf" ? (
              <iframe
                src={newFilePreview}
                title="PDF Preview"
                style={{
                  width: "500px",
                  height: "300px",
                  border: "2px dashed #007bff",
                  borderRadius: "8px",
                }}
              />
            ) : null}
          </div>
        )}

        {imageData &&
          imageData.map((item, index) => (
            <div key={index} style={{ margin: "10px", textAlign: "center" }}>
              <div
                style={{
                  marginBottom: "8px",
                  fontWeight: "bold",
                  fontSize: "12px",
                  color: "#333",
                }}
              >
                {item.header && item.footer && <span style={{ color: "#6f42c1" }}>Header & Footer</span>}
                {item.header && !item.footer && (
                  <span style={{ color: "#007bff", fontSize: "24px" }}>
                    Already Uploaded Header
                  </span>
                )}
                {!item.header && item.footer && (
                  <span style={{ color: "#28a745", fontSize: "24px" }}>
                    Already Uploaded Footer
                  </span>
                )}
                {item.watermark_image && <span style={{ color: "#6f42c1", marginLeft: 8 }}>Watermark</span>}
              </div>
              <img
                src={`data:${/\.jpe?g$/i.test(item.file_name || "") ? "image/jpeg" : /\.webp$/i.test(item.file_name || "") ? "image/webp" : "image/png"};base64,${item.file_data}`}
                alt="Header Footer Image"
                style={{ width: "500px" }}
              />
            </div>
          ))}
      </form>

      {showAdjust && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.6)",
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "28px",
              width: "600px",
              maxWidth: "95vw",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
            }}
          >
            <h5 style={{ marginBottom: "4px" }}>✂️ Adjust Image</h5>
            <p
              style={{ color: "#888", fontSize: "13px", marginBottom: "16px" }}
            >
              Image height is more than {MAX_HEIGHT}px. Please adjust to fit
              within {MAX_HEIGHT}px height.
            </p>

            <div className="mb-3">
              <label style={{ fontWeight: 600 }}>
                Zoom / Scale: <span style={{ color: "#007bff" }}>{scale}%</span>
              </label>
              <input
                type="range"
                className="form-range"
                min={10}
                max={200}
                value={scale}
                onChange={(e) => setScale(Number(e.target.value))}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "12px",
                  color: "#aaa",
                }}
              >
                <span>10%</span>
                <span>200%</span>
              </div>
            </div>

            <div className="mb-3">
              <label style={{ fontWeight: 600 }}>
                Vertical Position (Crop from top):{" "}
                <span style={{ color: "#007bff" }}>{cropY}%</span>
              </label>
              <input
                type="range"
                className="form-range"
                min={0}
                max={100}
                value={cropY}
                onChange={(e) => setCropY(Number(e.target.value))}
              />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "12px",
                  color: "#aaa",
                }}
              >
                <span>Top</span>
                <span>Bottom</span>
              </div>
            </div>

            <div className="mb-3" style={{ textAlign: "center" }}>
              <div
                style={{
                  fontWeight: 600,
                  marginBottom: "6px",
                  fontSize: "13px",
                  color: "#555",
                }}
              >
                Live Preview (output will be {MAX_HEIGHT}px tall):
              </div>
              <canvas
                ref={previewCanvasRef}
                style={{
                  border: "2px dashed #007bff",
                  borderRadius: "8px",
                  maxWidth: "100%",
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
              }}
            >
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowAdjust(false);
                  setFile(null);
                  setNewFilePreview(null);
                  setFileData("");
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={applyAdjustment}
              >
                ✅ Apply & Use
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UploadImages;
